import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  ChevronDown,
  Download,
  FileText,
  FileUp,
  Loader2,
  Palette,
  Sparkles,
} from "lucide-react";
import { AskCogoSurfaceButton } from "../components/ask-cogo-surface-button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useStudioLocale } from "@/i18n";
import { COOGO_IDE_CHROME_COPY } from "../coogo-ide-chrome-copy";
import { getSourceSurfaceCopy } from "./source-surface-copy";
import {
  StudioEditorHost,
  StudioEditorToolbar,
} from "../workspace/studio-editor-host";
import {
  OpenPencilFigmaSourcePanels,
  type SourceRightsEvidenceSelection,
} from "@/features/openpencil-figma/openpencil-figma-source-panels";
import { OpenPencilSourceTokens } from "@/features/openpencil-figma/openpencil-source-tokens";
import { OpenPencilSourceComponents } from "@/features/openpencil-figma/openpencil-source-components";
import { resolveOpenPencilDesignView } from "@/features/openpencil-figma/openpencil-source-design-view";
import {
  setSelectedSourceSurface,
  useSelectedSourceSurface,
} from "@/features/openpencil-figma/openpencil-source-selection";
import { DocxSourceViewer } from "@/features/source-document/docx-source-viewer";
import { getDocxSourceViewerCopy } from "@/features/source-document/docx-source-viewer-copy";
import {
  getDocxViewerSession,
  subscribeDocxViewerSession,
} from "@/features/source-document/docx-source-viewer-state";
import { DOCUMENT_SOURCE_EXTENSIONS } from "@/features/source-document/document-source-model";
import { buildOpenPencilSourceRightsDeclaration } from "@cogo/source-openpencil";
import {
  downloadOpenPencilSourceFile,
  importOpenPencilFigmaFile,
} from "@/features/openpencil-figma/openpencil-figma-client";
import { checkOpenPencilSourceFile } from "@/features/openpencil-figma/openpencil-source-file-gate";
import { describeOpenPencilSourceDurability } from "@/features/openpencil-figma/openpencil-source-restore";
import type { OpenPencilFigmaSourceBundle } from "@/features/openpencil-figma/openpencil-figma-source";
import {
  classifyOpenPencilImportFailureText,
  type OpenPencilImportFailureReason,
} from "@/features/openpencil-figma/openpencil-import-failure";
import { getOpenPencilImportFailureCopy } from "@/features/openpencil-figma/openpencil-import-failure-copy";
import { OpenPencilImportFailureNotice } from "@/features/openpencil-figma/openpencil-import-failure-notice";
import {
  downloadOpenPencilBrowserRevision,
  requestOpenPencilBrowserFirstRevision,
  type OpenPencilBrowserRevisionArtifact,
} from "@/features/openpencil-figma/openpencil-browser-revision-client";
import {
  applySuggestedSourceMapping,
  createOpenPencilSourceWorkspace,
  recordSourceRevisionVisualDiff,
  recordOpenPencilSourceRightsDeclaration,
  setAllSourceTokenDecisions,
  setSourcePageDecision,
  setSourceSectionDecision,
  setSourceSurfaceDecision,
  setSourceTokenDecision,
  type OpenPencilSourceWorkspace,
  type SourceDecisionStatus,
  type SourceSurfaceRole,
} from "@/features/openpencil-figma/openpencil-source-workspace";
import { compareOpenPencilRevisionVisuals } from "@/features/openpencil-figma/openpencil-revision-visual-diff";
import {
  buildOpenPencilSurfaceRevisionPatch,
  type OpenPencilSurfaceRevisionDraft,
} from "@/features/openpencil-figma/openpencil-source-revision";
import {
  cacheOpenPencilSourceWorkspaceLocally,
  getOpenPencilLocalPersistenceState,
  getOpenPencilProjectPersistenceState,
  loadOpenPencilSourceWorkspaceFromCurrentProject,
  saveOpenPencilSourceWorkspaceToCurrentProject,
} from "@/features/openpencil-figma/openpencil-source-workspace-storage";
import { registerCurrentProjectDesignSystem } from "@/features/design-system/lib/register-project-design-system";
import { deriveTenantId } from "@/store/auth-slice";
import { useStudioStore } from "@/store/studio-store";
import type { ChatContext } from "@/store/chat-types";
import { mcpBus } from "@/mcp/studio-mcp-bus";
import {
  explainSourceOperationError,
  type SourceRecoveryAction,
} from "@/features/source-ingest/source-ingest-model";
import { getSourceIngestCopy } from "@/features/source-ingest/source-ingest-copy";

/**
 * Where this import lives, said before the creator needs to know.
 *
 * An imported source used to be announced as lost only AFTER it was lost —
 * the workspace JSON was written to this browser's localStorage and nowhere
 * else, and the route was a fixed literal that named no import, so a second
 * device or a private window silently showed an empty canvas. This states the
 * situation while the work is in front of them.
 *
 * The verdict comes from `describeOpenPencilSourceDurability`, which reads the
 * bundle's own `sourceArtifactRef`. It is therefore a fact about this import,
 * not an optimistic default: a bundle with no server artifact reports
 * `restorable: false` and gets the browser-only line.
 */
function SourceDurabilityNotice({
  bundle,
  locale,
}: {
  bundle: OpenPencilFigmaSourceBundle | null | undefined;
  locale: string;
}) {
  if (!bundle) return null;
  const { restorable } = describeOpenPencilSourceDurability(bundle);
  const copy = getSourceSurfaceCopy(locale).durability;
  // Three real states, not two. A large import can be BOTH unrestorable and
  // rejected by localStorage (measured: QuotaExceededError on the 2.7 MB
  // audited .fig), and in that case "saved in this browser" is false.
  const localState = getOpenPencilLocalPersistenceState();
  const projectState = getOpenPencilProjectPersistenceState();
  const kind = projectState === "rejected"
    ? "projectNotSaved"
    : restorable
    ? "restorable"
    : localState === "rejected"
      ? "notSaved"
      : "browserOnly";
  return (
    <p
      className={
        kind === "restorable"
          ? "mb-3 rounded-md border border-border bg-muted/40 px-3 py-1.5 text-[11px] text-muted-foreground"
          : kind === "notSaved"
            ? "mb-3 rounded-md border border-destructive/50 bg-destructive/5 px-3 py-1.5 text-[11px] font-medium text-foreground"
            : "mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-[11px] font-medium text-amber-700 dark:text-amber-300"
      }
      data-testid="source-durability-notice"
      data-restorable={String(restorable)}
      data-durability={kind}
    >
      {copy[kind]}
    </p>
  );
}

function commitWorkspace(workspace: OpenPencilSourceWorkspace): void {
  useStudioStore.getState().setOpenPencilSourceWorkspace(workspace);
  void saveOpenPencilSourceWorkspaceToCurrentProject(workspace).catch((error) =>
    console.warn("[openpencil-source] workspace save failed", error),
  );
}

function currentWorkspace(): OpenPencilSourceWorkspace | null {
  return useStudioStore.getState().openPencilSourceWorkspace;
}

type BrowserRevisionAdmission = "pending" | "admitted" | "rejected";

interface BrowserRevisionWorkspaceBinding {
  projectId: string | null;
  sourceId: string;
  sha256: string;
}

interface BoundBrowserRevisionArtifact {
  artifact: OpenPencilBrowserRevisionArtifact;
  admission: BrowserRevisionAdmission;
  workspaces: BrowserRevisionWorkspaceBinding[];
}

function sourceChatContext(
  operation: string,
  workspace: OpenPencilSourceWorkspace | null,
): string {
  return JSON.stringify({
    surface: "source",
    operation,
    sourceId: workspace?.sourceId ?? null,
    revision: workspace?.revision ?? 0,
    sourceRevision: workspace?.bundle.revisionLineage?.sourceRevision ?? 0,
    generationAdmission:
      workspace?.bundle.generationAdmission.status ?? "blocked",
    requiredGateIds:
      workspace?.bundle.generationAdmission.requiredGateIds ?? [],
  });
}

export function SourceSurface() {
  const { locale } = useStudioLocale();
  const copy = COOGO_IDE_CHROME_COPY[locale];
  const sourceCopy = getSourceSurfaceCopy(locale);
  const documentCopy = getDocxSourceViewerCopy(locale);
  const sourceBundle = useStudioStore((s) => s.openPencilFigmaSource);
  const workspace = useStudioStore((s) => s.openPencilSourceWorkspace);
  // Read-only: the document viewer below owns this session; the toolbar only
  // needs the name so its subtitle can describe what is actually on screen.
  const documentSession = useSyncExternalStore(
    subscribeDocxViewerSession,
    getDocxViewerSession,
    getDocxViewerSession,
  );
  const openDocumentFileName = documentSession.model?.ok
    ? documentSession.model.fileName
    : null;
  const persona = useStudioStore((s) => s.studioPersona);
  const currentProjectId = useStudioStore((s) => s.currentProjectId);
  const projectSource = useStudioStore((s) => s.projectSource);
  const setPendingChatNotice = useStudioStore((s) => s.setPendingChatNotice);
  const importActivity = useStudioStore((s) => s.openPencilImportActivity);
  const setOpenPencilImportActivity = useStudioStore(
    (s) => s.setOpenPencilImportActivity,
  );
  const authorityDomain = useStudioStore((s) => s.chatContext?.authorityDomain);
  const authorityView = useStudioStore((s) => s.chatContext?.authorityView);
  // Canvas / Tokens / Components all live behind the same rail surface id, so
  // the active authority task is what decides which one is on screen.
  const designView = resolveOpenPencilDesignView({
    authorityDomain,
    authorityView,
  });
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const importInFlightRef = useRef(false);
  const revisionInFlightRef = useRef(false);
  const [importing, setImporting] = useState(false);
  const [revisionInFlight, setRevisionInFlight] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importStatus, setImportStatus] = useState<string | null>(null);
  const [sourceOperationDetail, setSourceOperationDetail] = useState<
    string | null
  >(null);
  const [sourceOperationMessage, setSourceOperationMessage] = useState<
    string | null
  >(null);
  const [sourceOperationRecovery, setSourceOperationRecovery] =
    useState<SourceRecoveryAction>(null);
  const [importFailure, setImportFailure] =
    useState<OpenPencilImportFailureReason | null>(null);
  const [revisionStatus, setRevisionStatus] = useState<string | null>(null);
  const [browserRevisionBinding, setBrowserRevisionBinding] =
    useState<BoundBrowserRevisionArtifact | null>(null);
  const [designSystemRegistering, setDesignSystemRegistering] = useState(false);
  const [designSystemNotice, setDesignSystemNotice] = useState<string | null>(
    null,
  );
  const [registeredDesignSystemId, setRegisteredDesignSystemId] = useState<
    string | null
  >(null);
  const [activeResourceKind, setActiveResourceKind] = useState<
    "design" | "document"
  >("design");
  const activeBundle = workspace?.bundle ?? sourceBundle;
  const hasReadableSource = Boolean(
    activeBundle?.readback && activeBundle.sourceArtifactRef,
  );
  // Shared by the canvas and the Components screen. Keyed by source id so it
  // outlives a switch between them without ever leaking across design files.
  const activeSourceId = activeBundle?.sourceId ?? "";
  const selectedSurfaceId = useSelectedSourceSurface(activeSourceId);
  const setSelectedSurfaceId = useCallback(
    (surfaceId: string) => setSelectedSourceSurface(activeSourceId, surfaceId),
    [activeSourceId],
  );
  const visibleBrowserRevisionBinding =
    browserRevisionBinding && activeBundle
      ? browserRevisionBinding.workspaces.some(
          (binding) =>
            binding.projectId === currentProjectId &&
            binding.sourceId === activeBundle.sourceId &&
            binding.sha256 === activeBundle.fileSha256,
        )
        ? browserRevisionBinding
        : null
      : null;

  useEffect(() => {
    if (openDocumentFileName && !activeBundle) {
      setActiveResourceKind("document");
    }
  }, [activeBundle, openDocumentFileName]);

  useEffect(() => {
    if (activeBundle) {
      setActiveResourceKind("design");
    }
  }, [activeBundle?.sourceId]);

  const visibleResourceKind =
    designView === "canvas" ? activeResourceKind : "design";
  const sourceDurability = activeBundle
    ? describeOpenPencilSourceDurability(activeBundle)
    : null;
  const sourcePersistenceRejected =
    Boolean(activeBundle) &&
    !sourceDurability?.restorable &&
    getOpenPencilLocalPersistenceState() === "rejected";
  // The three honest canvas states (UX audit, 2026-07-27). `importInFlight`
  // also reflects an import started on another surface, so navigating here
  // mid-import shows progress instead of a blank page.
  const importInFlight = importing || importActivity?.phase === "importing";
  const blockedWithoutSource: OpenPencilImportFailureReason | null =
    !sourceBundle && !workspace
      ? (importFailure ??
        (importActivity?.phase === "blocked"
          ? (importActivity.reason ?? "unknown")
          : null))
      : null;
  const sourceWorkspaceId = workspace?.sourceId ?? sourceBundle?.sourceId;
  const sourceRevision =
    workspace?.bundle.revisionLineage?.sourceRevision ??
    sourceBundle?.revisionLineage?.sourceRevision ??
    workspace?.revision;
  const sourceContextPatch: Partial<ChatContext> = {
    surfaceId: "source",
    ...(sourceWorkspaceId ? { sourceWorkspaceId } : {}),
    ...(typeof sourceRevision === "number" ? { sourceRevision } : {}),
  };
  useEffect(() => {
    if (workspace) return;
    void loadOpenPencilSourceWorkspaceFromCurrentProject().catch((error) =>
      console.warn("[openpencil-source] workspace load failed", error),
    );
  }, [currentProjectId, workspace]);

  // Keeps the shared frame selection pointing at a frame that still exists,
  // and seeds it when Components is the first Design screen opened.
  useEffect(() => {
    const surfaces = activeBundle?.sourceFacts?.surfaceCandidates ?? [];
    if (surfaces.some((surface) => surface.id === selectedSurfaceId)) return;
    setSelectedSurfaceId(surfaces[0]?.id ?? "");
  }, [activeBundle, selectedSurfaceId, setSelectedSurfaceId]);

  useEffect(() => {
    setBrowserRevisionBinding((current) => {
      if (!current || !activeBundle) return null;
      return current.workspaces.some(
        (binding) =>
          binding.projectId === currentProjectId &&
          binding.sourceId === activeBundle.sourceId &&
          binding.sha256 === activeBundle.fileSha256,
      )
        ? current
        : null;
    });
  }, [activeBundle?.fileSha256, activeBundle?.sourceId, currentProjectId]);

  function updatePage(
    pageId: string,
    status: SourceDecisionStatus,
    storyTitle?: string,
  ) {
    const current = currentWorkspace();
    if (!current) return;
    commitWorkspace(
      setSourcePageDecision(current, {
        pageId,
        status,
        ...(storyTitle ? { storyTitle } : {}),
        actor: "human",
      }),
    );
  }

  function updateSection(
    sectionId: string,
    status: SourceDecisionStatus,
    storyTitle?: string,
  ) {
    const current = currentWorkspace();
    if (!current) return;
    commitWorkspace(
      setSourceSectionDecision(current, {
        sectionId,
        status,
        ...(storyTitle ? { storyTitle } : {}),
        actor: "human",
      }),
    );
  }

  function updateSurface(
    surfaceId: string,
    status: SourceDecisionStatus,
    role: SourceSurfaceRole,
  ) {
    const current = currentWorkspace();
    if (!current) return;
    const previous = current.surfaceDecisions[surfaceId];
    commitWorkspace(
      setSourceSurfaceDecision(current, {
        ...previous,
        surfaceId,
        status,
        role,
        actor: "human",
      }),
    );
  }

  function updateToken(
    tokenId: string,
    status: SourceDecisionStatus,
    canonicalToken?: string,
  ) {
    const current = currentWorkspace();
    if (!current) return;
    commitWorkspace(
      setSourceTokenDecision(current, {
        tokenId,
        status,
        ...(canonicalToken ? { canonicalToken } : {}),
        actor: "human",
      }),
    );
  }

  /**
   * Register the accepted token candidates as this project's design system.
   * Every refusal is shown verbatim from the server's typed code — this is the
   * step that used to be missing entirely, so a silent failure here would
   * recreate exactly the "counter moved, nothing happened" problem.
   */
  async function registerProjectDesignSystem() {
    if (designSystemRegistering) return;
    setDesignSystemRegistering(true);
    setDesignSystemNotice(null);
    try {
      const outcome = await registerCurrentProjectDesignSystem();
      if (outcome.ok) {
        setRegisteredDesignSystemId(outcome.registration.designSystemId);
        // R1 — publish it to the store so THIS session can generate against
        // the imported design immediately. Reloading the project rehydrates
        // the same record from `settings.projectDesignSystem`; without this
        // write the user would have to reload before generation stopped
        // refusing, which reads as the button having done nothing.
        useStudioStore
          .getState()
          .setProjectDesignSystemRegistration(outcome.registration);
        const skipped =
          outcome.registration.derivation.unprojectedTokens.length;
        setDesignSystemNotice(
          // Same verb as the button that got here ("Apply these to this
          // project"), so the result is legible as that action's outcome.
          sourceCopy.status.designSystemApplied(
            outcome.registration.derivation.projectedTokenCount,
            skipped,
          ),
        );
      } else {
        setDesignSystemNotice(outcome.message);
      }
    } finally {
      setDesignSystemRegistering(false);
    }
  }

  async function importSource(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (
      !file ||
      importing ||
      importInFlightRef.current ||
      revisionInFlightRef.current
    )
      return;

    // UX audit finding 4 — reject a forced non-source file BEFORE upload, so
    // its name can never become a Design source title.
    const gate = checkOpenPencilSourceFile({ name: file.name, size: file.size });
    if (!gate.ok) {
      setImportFailure(gate.reason);
      setImportStatus(null);
      setOpenPencilImportActivity({
        phase: "blocked",
        fileName: file.name,
        reason: gate.reason,
        title: getOpenPencilImportFailureCopy(locale, gate.reason).title,
        startedAt: Date.now(),
        updatedAt: Date.now(),
      });
      return;
    }

    importInFlightRef.current = true;
    setImporting(true);
    setImportFailure(null);
    setBrowserRevisionBinding(null);
    setImportStatus(sourceCopy.states.preparingImport);
    const startedAt = Date.now();
    setOpenPencilImportActivity({
      phase: "importing",
      fileName: file.name,
      statusLabel: sourceCopy.states.preparingImport,
      startedAt,
      updatedAt: startedAt,
    });
    let failureReason: OpenPencilImportFailureReason | null = null;
    let failureDetail: string | null = null;
    try {
      const bundle = await importOpenPencilFigmaFile({
        file,
        sourceName: file.name.replace(/\.(fig|pen)$/i, ""),
        workspaceId: currentProjectId ?? "workspace_local",
        onStatus: (status) => {
          setImportStatus(status);
          setOpenPencilImportActivity({
            phase: "importing",
            fileName: file.name,
            statusLabel: status,
            startedAt,
            updatedAt: Date.now(),
          });
        },
        onFailure: (failure) => {
          failureReason = failure.reason;
          failureDetail = failure.detail;
        },
      });
      const result = await mcpBus.executeTrustedUi({
        tool: "cogo.studio.import_openpencil_figma_source",
        params: {
          sourceBundle: bundle,
          openInSourceSurface: true,
        },
      });
      if (!result.success) {
        // Registration failed after a readable import — still a blocker, and
        // still creator copy rather than the raw tool error.
        const reason: OpenPencilImportFailureReason =
          failureReason ?? "unknown";
        setImportFailure(reason);
        setImportStatus(null);
        setOpenPencilImportActivity({
          phase: "blocked",
          fileName: file.name,
          reason,
          title: getOpenPencilImportFailureCopy(locale, reason).title,
          detail: result.error ?? failureDetail ?? undefined,
          startedAt,
          updatedAt: Date.now(),
        });
        return;
      }
      // Generation admission can remain blocked after a successful .fig
      // readback. That blocks generation, not direct browser editing/export.
      if (!bundle.readback || !bundle.sourceArtifactRef) {
        const reason: OpenPencilImportFailureReason =
          failureReason ??
          classifyOpenPencilImportFailureText(bundle.blockedEvidence?.reason);
        setImportFailure(reason);
        setImportStatus(null);
        setOpenPencilImportActivity({
          phase: "blocked",
          fileName: file.name,
          reason,
          title: getOpenPencilImportFailureCopy(locale, reason).title,
          detail: failureDetail ?? bundle.blockedEvidence?.reason,
          startedAt,
          updatedAt: Date.now(),
        });
        return;
      }
      setImportFailure(null);
      setOpenPencilImportActivity(null);
      setImportStatus(
        sourceCopy.status.designRead(
          bundle.sourceFacts?.documentProfile.pages ?? 0,
          bundle.sourceFacts?.documentProfile.totalNodes ?? 0,
        ),
      );
    } catch (error) {
      const reason: OpenPencilImportFailureReason = failureReason ?? "unknown";
      setImportFailure(reason);
      setImportStatus(null);
      setOpenPencilImportActivity({
        phase: "blocked",
        fileName: file.name,
        reason,
        title: getOpenPencilImportFailureCopy(locale, reason).title,
        detail:
          failureDetail ?? (error instanceof Error ? error.message : undefined),
        startedAt,
        updatedAt: Date.now(),
      });
    } finally {
      importInFlightRef.current = false;
      setImporting(false);
    }
  }

  async function exportSource() {
    if (!activeBundle || exporting) return;
    setExporting(true);
    setSourceOperationDetail(null);
    setSourceOperationMessage(null);
    setSourceOperationRecovery(null);
    try {
      const exported = await downloadOpenPencilSourceFile(activeBundle);
      setImportStatus(
        sourceCopy.status.exported(
          exported.fileName,
          exported.sha256.slice(0, 12),
        ),
      );
      setPendingChatNotice(
        sourceCopy.status.exportedChatNotice(exported.fileName),
      );
    } catch (error) {
      const explained = explainSourceOperationError(error);
      const ingestCopy = getSourceIngestCopy(locale);
      const message = ingestCopy.operationError[explained.messageKey];
      setImportStatus(message);
      setSourceOperationMessage(message);
      setSourceOperationDetail(explained.technicalDetail);
      setSourceOperationRecovery(
        explained.messageKey === "sign-in" ||
          explained.messageKey === "permission"
          ? "sign-in"
          : explained.messageKey === "retry"
            ? "retry"
            : null,
      );
      setPendingChatNotice(message);
    } finally {
      setExporting(false);
    }
  }

  async function recordSourceRightsEvidence(
    selection: SourceRightsEvidenceSelection,
  ): Promise<void> {
    // Frozen with the rest of the inputs this declaration is sealed over.
    //
    // `declaredAt` is not a wall-clock stamper to be replaced by a derived
    // value: it is WHEN A HUMAN DECLARED THESE RIGHTS, and making it
    // content-derived would make the bytes deterministic and the field a lie.
    // The question is only which component may honestly read the clock for it.
    // F0 reproduces a PAST operator designation and cannot know it, so it fails
    // closed on `--declared-at`. This path is reached from the rights panel's
    // submit button — disabled until the user picks a basis, invoked from its
    // `onClick` (openpencil-figma-source-panels.tsx) — so the call IS the
    // human's confirmation, and reading the clock here is the observation, not
    // an invention. Read before the workspace snapshot so the stamp cannot land
    // after the state it describes.
    const declaredAt = new Date().toISOString();
    const state = useStudioStore.getState();
    const current = state.openPencilSourceWorkspace;
    const projectId = state.currentProjectId;
    const tenantId = deriveTenantId(state.session);
    const principalId = state.session?.user?.id;
    if (!current) {
      throw new Error(sourceCopy.errors.importBeforeRecordingEvidence);
    }
    if (
      state.projectSource !== "server" ||
      !projectId ||
      !tenantId ||
      !principalId
    ) {
      throw new Error(sourceCopy.errors.signInBeforeRecordingEvidence);
    }
    const declaration = await buildOpenPencilSourceRightsDeclaration({
      bundle: current.bundle,
      sourceRevision:
        current.bundle.revisionLineage?.sourceRevision ?? 0,
      scope: {
        tenantId,
        projectId,
        principalId,
      },
      rightsBasis:
        selection.kind === "evaluation-only"
          ? "unverified"
          : selection.kind,
      requestedUse:
        selection.kind === "evaluation-only"
          ? "evaluation-only"
          : "project-app-and-pack-review",
      ...(selection.kind === "licensed"
        ? { licenseRef: selection.licenseRef }
        : {}),
      declaredAt,
    });
    const latest = useStudioStore.getState();
    if (
      latest.openPencilSourceWorkspace !== current ||
      latest.currentProjectId !== projectId ||
      deriveTenantId(latest.session) !== tenantId ||
      latest.session?.user?.id !== principalId
    ) {
      throw new Error(sourceCopy.errors.projectChangedBeforeEvidence);
    }
    const next = await recordOpenPencilSourceRightsDeclaration(
      current,
      declaration,
    );
    useStudioStore.getState().setOpenPencilSourceWorkspace(next);
    try {
      const persisted = await saveOpenPencilSourceWorkspaceToCurrentProject(
        next,
      );
      if (!persisted) {
        throw new Error(sourceCopy.errors.evidenceNotPersisted);
      }
    } catch (error) {
      if (useStudioStore.getState().openPencilSourceWorkspace === next) {
        useStudioStore.getState().setOpenPencilSourceWorkspace(current);
        cacheOpenPencilSourceWorkspaceLocally(current, projectId);
      }
      throw error;
    }
    setImportStatus(
      selection.kind === "evaluation-only"
        ? sourceCopy.status.evaluationOnlyEvidenceRecorded
        : sourceCopy.status.sourceUseEvidenceRecorded,
    );
  }

  async function reviseSurface(draft: OpenPencilSurfaceRevisionDraft) {
    if (revisionInFlightRef.current || importInFlightRef.current) {
      throw new Error(sourceCopy.errors.importOrRevisionRunning);
    }
    revisionInFlightRef.current = true;
    setRevisionInFlight(true);
    let browserArtifact: OpenPencilBrowserRevisionArtifact | null = null;
    try {
      const current = currentWorkspace();
      if (!current) throw new Error(sourceCopy.errors.importBeforeEditing);
      if (current.candidateBinding) {
        throw new Error(sourceCopy.errors.alreadyBoundToCandidate);
      }
      const patch = buildOpenPencilSurfaceRevisionPatch({
        bundle: current.bundle,
        draft,
      });
      const baseProjectId = useStudioStore.getState().currentProjectId;
      const baseBinding: BrowserRevisionWorkspaceBinding = {
        projectId: baseProjectId,
        sourceId: patch.baseSourceId,
        sha256: patch.baseSha256,
      };
      const assertBaseWorkspaceCurrent = () => {
        const state = useStudioStore.getState();
        if (
          state.currentProjectId !== baseProjectId ||
          state.openPencilSourceWorkspace !== current
        ) {
          throw new Error(sourceCopy.errors.projectChangedDuringRevision);
        }
      };
      setBrowserRevisionBinding(null);
      setRevisionStatus(sourceCopy.status.applyingBoundedEdits);
      const result = await requestOpenPencilBrowserFirstRevision({
        bundle: current.bundle,
        patch,
        onStatus: setRevisionStatus,
        onBrowserArtifact: (artifact) => {
          browserArtifact = artifact;
          setBrowserRevisionBinding({
            artifact,
            admission: "pending",
            workspaces: [baseBinding],
          });
        },
        beforeCanonicalCommit: assertBaseWorkspaceCurrent,
      });
      assertBaseWorkspaceCurrent();
      setRevisionStatus(sourceCopy.status.comparingPreviews);
      const visualDiff = await compareOpenPencilRevisionVisuals(
        current.bundle,
        result.bundle,
      );
      const nextWorkspace = recordSourceRevisionVisualDiff(
        createOpenPencilSourceWorkspace(result.bundle),
        visualDiff,
      );
      assertBaseWorkspaceCurrent();
      if (result.browserArtifact) {
        setBrowserRevisionBinding({
          artifact: result.browserArtifact,
          admission: "admitted",
          workspaces: [
            baseBinding,
            {
              projectId: baseProjectId,
              sourceId: result.bundle.sourceId,
              sha256: result.evidence.outputSha256,
            },
          ],
        });
      }
      commitWorkspace(nextWorkspace);
      const revisionNumber =
        nextWorkspace.bundle.revisionLineage?.sourceRevision ??
        nextWorkspace.revision;
      const changedSurfaces = visualDiff.changedSurfaceIds.length;
      // Branch on the PROOF OUTCOME, not on whether an artifact happens to be
      // present: saying "browser export/reopen verified" when no proof was
      // taken is the claim this whole change exists to stop making.
      setRevisionStatus(
        result.browserProof.status === "verified"
          ? sourceCopy.status.browserRevisionVerified(
              revisionNumber,
              changedSurfaces,
            )
          : sourceCopy.status.serverRevisionVerified(
              revisionNumber,
              changedSurfaces,
            ),
      );
    } catch (error) {
      if (browserArtifact) {
        setBrowserRevisionBinding((current) =>
          current?.artifact === browserArtifact
            ? { ...current, admission: "rejected" }
            : current,
        );
      }
      const message =
        error instanceof Error
          ? error.message
          : sourceCopy.status.revisionFailed;
      setRevisionStatus(message);
      throw error;
    } finally {
      revisionInFlightRef.current = false;
      setRevisionInFlight(false);
    }
  }

  return (
    <StudioEditorHost
      testId="studio-source-surface"
      toolbar={
        <StudioEditorToolbar
          title={
            visibleResourceKind === "document"
              ? documentCopy.header.title
              : designView === "tokens"
              ? sourceCopy.toolbar.titleTokens
              : designView === "components"
                ? sourceCopy.toolbar.titleComponents
                : sourceCopy.toolbar.titleDesign
          }
          // The Canvas screen hosts BOTH the `.fig` source workspace and the
          // document viewer, so the fallback may not name only `.fig`.
          // Measured 2026-07-30: with a 1.6 MB `.docx` open and rendered right
          // below, this line still read "Import a .fig source" — a header
          // describing a different source than the one on screen. When a
          // document is open and no design is, the document names itself.
          subtitle={
            visibleResourceKind === "document"
              ? (openDocumentFileName ??
                documentCopy.empty.title(
                  DOCUMENT_SOURCE_EXTENSIONS.join(" / "),
                ))
              : (workspace?.bundle.fileName ??
                sourceBundle?.fileName ??
                sourceCopy.toolbar.subtitleFallback)
          }
          actions={
            <div
              // Capacity guard (review finding, post-A3/B2): this wrapper
              // must stay flex-nowrap + shrink-0. It sits as a single flex
              // item inside StudioEditorToolbar's own actions cell, which is
              // already flex-nowrap + sm:overflow-x-auto — but flex-wrap
              // here would let *this* div's own children reflow onto a
              // second line and grow past the toolbar's fixed sm:h-10 box,
              // bleeding over the canvas underneath (the exact canary
              // failure class, reproduced via the vertical axis instead of
              // the horizontal one). shrink-0 keeps this row at its natural
              // width so the toolbar cell's overflow-x-auto is what handles
              // any overflow, never internal wrapping. See
              // canary-investigation.md.
              className="flex flex-nowrap shrink-0 items-center gap-1.5"
              data-testid="source-chat-ops"
            >
              {visibleResourceKind === "design" ? (
                <>
                  <input
                    ref={importInputRef}
                    type="file"
                    accept=".fig,.pen"
                    tabIndex={-1}
                    className="sr-only"
                    data-testid="source-figma-file-input"
                    onChange={(event) => {
                      void importSource(event);
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => importInputRef.current?.click()}
                    disabled={importing || revisionInFlight}
                    className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-background px-2 text-[11px] font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {importing ? (
                      <Loader2
                        className="h-3.5 w-3.5 animate-spin"
                        aria-hidden
                      />
                    ) : (
                      <FileUp className="h-3.5 w-3.5" aria-hidden />
                    )}
                    {importing
                      ? sourceCopy.toolbar.importing
                      : sourceCopy.toolbar.importFig}
                  </button>
                </>
              ) : null}
              {visibleResourceKind === "design" ? (
                <AskCogoSurfaceButton
                  label={sourceCopy.toolbar.editAppAndSource}
                  operationId="source.figma.propose_design_mutation"
                  operationKind="source-design-mutation-proposal"
                  className="h-7 border-foreground bg-foreground px-2 text-[11px] text-background shadow-none hover:bg-foreground/90"
                  chatContextPatch={sourceContextPatch}
                  context={() =>
                    sourceChatContext(
                      "apply-design-mutation",
                      currentWorkspace(),
                    )
                  }
                />
              ) : null}
              {visibleResourceKind === "design" ? <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="source-review-menu-trigger"
                    className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-background px-2 text-[11px] font-medium text-foreground transition-colors hover:bg-muted"
                  >
                    {copy.sourceReviewMenu}
                    <ChevronDown className="h-3 w-3" aria-hidden />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-56 p-1"
                  data-testid="source-review-menu-content"
                >
                  {/* Consolidated Ask-Coogo source actions (A3/B2 canary
                      regression root fix): the Source toolbar's dense
                      6-action row could overflow past its bounds at desktop
                      widths and get painted over by canvas content beneath
                      it. Each item below preserves the exact operationId/
                      operationKind wiring of the button it replaces. */}
                  <AskCogoSurfaceButton
                    label={sourceCopy.toolbar.inspectSource}
                    operationId="source.figma.inspect"
                    operationKind="source-readback"
                    className="h-8 w-full justify-start border-none bg-transparent px-2 text-xs shadow-none hover:bg-muted"
                    chatContextPatch={sourceContextPatch}
                    context={() =>
                      sourceChatContext("inspect", currentWorkspace())
                    }
                  />
                  <AskCogoSurfaceButton
                    label={sourceCopy.toolbar.visualReview}
                    operationId="source.figma.visual_review"
                    operationKind="source-visual-evidence"
                    className="h-8 w-full justify-start border-none bg-transparent px-2 text-xs shadow-none hover:bg-muted"
                    chatContextPatch={sourceContextPatch}
                    context={() =>
                      sourceChatContext("visual-review", currentWorkspace())
                    }
                  />
                </PopoverContent>
              </Popover> : null}
              {visibleResourceKind === "design" ? <button
                type="button"
                onClick={() => void exportSource()}
                disabled={!activeBundle || exporting}
                className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-background px-2 text-[11px] font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
              >
                {exporting ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : (
                  <Download className="h-3.5 w-3.5" aria-hidden />
                )}
                {exporting
                  ? sourceCopy.toolbar.exporting
                  : sourceCopy.toolbar.exportSource}
              </button> : null}
              {visibleResourceKind === "design" && visibleBrowserRevisionBinding ? (
                <button
                  type="button"
                  onClick={() =>
                    downloadOpenPencilBrowserRevision(
                      visibleBrowserRevisionBinding.artifact,
                    )
                  }
                  className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-background px-2 text-[11px] font-medium text-foreground hover:bg-muted"
                >
                  <Download className="h-3.5 w-3.5" aria-hidden />
                  {sourceCopy.toolbar.exportBrowserFig}
                </button>
              ) : null}
            </div>
          }
        />
      }
    >
      <div className="h-full overflow-auto bg-background p-3 sm:p-6">
        <div className="mx-auto w-full min-w-0 max-w-6xl">
          {designView === "canvas" ? (
            <div
              className="mb-3 flex flex-wrap items-center justify-between gap-2"
              data-testid="source-resource-switcher"
            >
              <div
                className="inline-flex rounded-lg border border-border bg-muted/40 p-1"
                role="group"
                aria-label={sourceCopy.resources.label}
              >
                <button
                  type="button"
                  aria-pressed={activeResourceKind === "design"}
                  onClick={() => setActiveResourceKind("design")}
                  className={`inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-medium transition-colors ${
                    activeResourceKind === "design"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Palette className="h-3.5 w-3.5" aria-hidden />
                  {sourceCopy.resources.design}
                </button>
                <button
                  type="button"
                  aria-pressed={activeResourceKind === "document"}
                  onClick={() => setActiveResourceKind("document")}
                  className={`inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-medium transition-colors ${
                    activeResourceKind === "document"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <FileText className="h-3.5 w-3.5" aria-hidden />
                  {sourceCopy.resources.document}
                </button>
              </div>
              {activeBundle ? (
                <details
                  className="group rounded-lg border border-border bg-card px-3 py-2 text-xs"
                  open={sourcePersistenceRejected || undefined}
                  data-testid="source-details-disclosure"
                >
                  <summary className="flex cursor-pointer list-none items-center gap-1.5 font-medium text-foreground">
                    <Sparkles className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                    {sourceCopy.resources.details}
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground transition-transform group-open:rotate-180" aria-hidden />
                  </summary>
                  <div className="mt-2 max-w-xl">
                    <SourceDurabilityNotice bundle={activeBundle} locale={locale} />
                  </div>
                </details>
              ) : null}
            </div>
          ) : (
            <SourceDurabilityNotice bundle={activeBundle} locale={locale} />
          )}
          {activeBundle &&
          hasReadableSource &&
          designView === "tokens" ? (
            <OpenPencilSourceTokens
              bundle={activeBundle}
              workspace={workspace}
              onTokenDecision={updateToken}
              onBulkTokenDecision={(status) => {
                const current = currentWorkspace();
                if (current) {
                  commitWorkspace(
                    setAllSourceTokenDecisions(current, {
                      status,
                      actor: "human",
                      reason: "Bulk token decision from Studio.",
                    }),
                  );
                }
              }}
              onRegisterProjectDesignSystem={
                projectSource === "server" && currentProjectId
                  ? () => void registerProjectDesignSystem()
                  : undefined
              }
              registerProjectDesignSystemPending={designSystemRegistering}
              projectDesignSystemNotice={designSystemNotice}
              registeredProjectDesignSystemId={registeredDesignSystemId}
            />
          ) : activeBundle &&
            hasReadableSource &&
            designView === "components" ? (
            <OpenPencilSourceComponents
              bundle={activeBundle}
              selectedSurfaceId={selectedSurfaceId}
              onSelectSurface={setSelectedSurfaceId}
            />
          ) : visibleResourceKind === "document" ? (
            <DocxSourceViewer />
          ) : sourceBundle || workspace ? (
            <OpenPencilFigmaSourcePanels
              bundle={activeBundle}
              selectedSurfaceId={selectedSurfaceId}
              onSelectedSurfaceIdChange={setSelectedSurfaceId}
              workspace={workspace}
              showDeveloperDetails={persona === "developer"}
              onExportSource={async () => {
                await exportSource();
              }}
              onReviseSurface={reviseSurface}
              revisionInFlight={revisionInFlight}
              revisionStatus={revisionStatus}
              browserRevisionProof={
                visibleBrowserRevisionBinding?.artifact.proof
              }
              browserRevisionAdmission={
                visibleBrowserRevisionBinding?.admission
              }
              onApplySuggestedMapping={() => {
                const current = currentWorkspace();
                if (current) {
                  commitWorkspace(
                    applySuggestedSourceMapping(current, {
                      actor: "human",
                      reason: "Accepted Studio geometry suggestions.",
                    }),
                  );
                }
              }}
              onPageDecision={updatePage}
              onSectionDecision={updateSection}
              onSurfaceDecision={updateSurface}
              onTokenDecision={updateToken}
              onBulkTokenDecision={(status) => {
                const current = currentWorkspace();
                if (current) {
                  commitWorkspace(
                    setAllSourceTokenDecisions(current, {
                      status,
                      actor: "human",
                      reason: "Bulk token decision from Studio.",
                    }),
                  );
                }
              }}
              onRegisterProjectDesignSystem={
                projectSource === "server" && currentProjectId
                  ? () => void registerProjectDesignSystem()
                  : undefined
              }
              registerProjectDesignSystemPending={designSystemRegistering}
              projectDesignSystemNotice={designSystemNotice}
              registeredProjectDesignSystemId={registeredDesignSystemId}
              onRecordSourceRightsEvidence={recordSourceRightsEvidence}
              importFailureReason={importFailure}
              onRetryImport={() => importInputRef.current?.click()}
            />
          ) : importInFlight ? (
            /* UX audit finding 1 — an import in flight must never paint an
               empty canvas. This branch also covers an import started on
               another surface, because the phase lives in the store. */
            <div
              className="flex min-h-[360px] items-center justify-center rounded-md border border-dashed border-border bg-card/40 px-6 py-10 text-center"
              data-testid="source-importing-state"
              aria-live="polite"
            >
              <div className="max-w-sm">
                <Loader2
                  className="mx-auto h-8 w-8 animate-spin text-muted-foreground"
                  aria-hidden
                />
                <h2 className="mt-4 text-sm font-semibold text-foreground">
                  {sourceCopy.states.importingTitle}
                </h2>
                {importActivity?.fileName ? (
                  <p
                    className="mt-1 truncate text-xs text-muted-foreground"
                    data-testid="source-importing-file"
                  >
                    {importActivity.fileName}
                  </p>
                ) : null}
                <p className="mt-2 text-xs leading-5 text-muted-foreground">
                  {importActivity?.statusLabel ??
                    importStatus ??
                    sourceCopy.states.preparingImport}
                </p>
              </div>
            </div>
          ) : blockedWithoutSource ? (
            /* A failure that produced no bundle at all (rejected file type,
               signed-out, transport failure before registration). Without
               this branch the canvas was blank and the creator waited. */
            <div data-testid="source-blocked-state">
              <OpenPencilImportFailureNotice
                reason={blockedWithoutSource}
                fileName={importActivity?.fileName ?? null}
                detail={importActivity?.detail ?? null}
                showDeveloperDetails={persona === "developer"}
                onRetry={() => importInputRef.current?.click()}
                retryLabel={sourceCopy.states.retryChooseAnother}
                testId="source-import-failure"
              />
            </div>
          ) : (
            <div
              className="flex min-h-[360px] items-center justify-center rounded-md border border-dashed border-border bg-card/40 px-6 py-10 text-center"
              data-testid="source-empty-state"
            >
              <div className="max-w-sm">
                <FileUp
                  className="mx-auto h-8 w-8 text-muted-foreground"
                  aria-hidden
                />
                <h2 className="mt-4 text-sm font-semibold text-foreground">
                  {sourceCopy.states.emptyTitle}
                </h2>
                <p className="mt-2 text-xs leading-5 text-muted-foreground">
                  {sourceCopy.states.emptyBody}
                </p>
                <button
                  type="button"
                  onClick={() => importInputRef.current?.click()}
                  disabled={importing || revisionInFlight}
                  className="mt-4 inline-flex h-8 items-center gap-1.5 rounded-md bg-foreground px-3 text-xs font-medium text-background transition-colors hover:bg-foreground/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FileUp className="h-3.5 w-3.5" aria-hidden />
                  {sourceCopy.states.emptyAction}
                </button>
              </div>
            </div>
          )}
          {importStatus ? (
            <div
              className="mt-3 rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground"
              aria-live="polite"
              data-testid="source-import-status"
            >
              <div className="flex flex-wrap items-center gap-3">
                <p className="min-w-0 flex-1">{importStatus}</p>
                {sourceOperationMessage === importStatus &&
                sourceOperationRecovery ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (sourceOperationRecovery === "sign-in") {
                        window.location.assign("/login");
                      } else {
                        void exportSource();
                      }
                    }}
                    className="inline-flex min-h-9 items-center rounded-md bg-foreground px-3 text-xs font-semibold text-background hover:bg-foreground/90"
                  >
                    {
                      getSourceIngestCopy(locale).recovery[
                        sourceOperationRecovery
                      ]
                    }
                  </button>
                ) : null}
              </div>
              {sourceOperationMessage === importStatus &&
              sourceOperationDetail ? (
                <details className="mt-2">
                  <summary className="cursor-pointer font-medium text-foreground">
                    {getSourceIngestCopy(locale).technicalDetails}
                  </summary>
                  <p className="mt-1 break-words font-mono text-[11px] leading-5">
                    {sourceOperationDetail}
                  </p>
                </details>
              ) : null}
            </div>
          ) : null}
          {/* WS-D0b — additive "보기" leg of the source-verification axiom: a
              locally-parsed DOCX normalized-structure view, independent of the
              .fig import flow above (never alters its default behavior).
              Scoped to the Canvas screen so Tokens/Components stay about the
              design file's own contents. */}
        </div>
      </div>
    </StudioEditorHost>
  );
}
